import { z } from 'zod';
import { BriefItemKind } from './brief-item-kind.js';
import { BriefItemDisposition } from './brief-item-disposition.js';
/**
 * Citation tuple used inside a BriefItem.body — `<Cite src="sf" n={2114} />`
 * renders the chip. Tuple shape so it's compact in storage.
 */
export const BriefItemCitation = z.tuple([z.string(), z.union([z.number().int(), z.string()])]);
/**
 * Suggested action surfaced under a brief item — wires into the
 * action ledger when the user picks one (see brief-action.ts).
 *
 * `verb` is intentionally free-form (z.string()) rather than constrained
 * to the BriefAction enum: the synthesis layer needs flexibility to
 * propose tool-specific verbs (e.g. 'reply-with-pricing', 'snooze-1w')
 * that don't map 1:1 onto the 14-verb closed UI action set. The
 * disposition transition lives on BriefAction (closed); the proposed
 * action label is open.
 */
export const BriefItemSuggestedAction = z.object({
    verb: z.string(), // 'approve' / 'decline' / 'draft' — free-form for synthesis flexibility
    label: z.string(), // CTA copy ('Approve & send')
    target_tool: z.string().optional(), // e.g. 'gmail.send.drafts' / 'calendar.move_event' / 'sf.update'
    scope: z.string().optional(), // e.g. 'pricing.rollout'
});
/**
 * One item inside a Brief. Items are ordered by the synthesis ranker;
 * the ordering is preserved in the array on the Brief doc.
 */
export const BriefItem = z.object({
    id: z.string(), // 'itm_decision_01' / 'itm_watch_02' / etc.
    kind: BriefItemKind,
    title: z.string().max(280), // serif headline
    body: z.string().max(2000), // paragraph
    citations: z.array(BriefItemCitation).default([]),
    confidence: z.number().min(0).max(1), // drives ConfidenceMeter + FB-04 inline prompt when <0.7
    suggested_actions: z.array(BriefItemSuggestedAction).default([]),
    /**
     * Persistent UI state. Updated by the action ledger event-writer
     * when the user approves/declines/defers/ignores.
     */
    disposition: BriefItemDisposition.default(BriefItemDisposition.enum.PENDING),
    /** Optional resolution timestamp (set when disposition leaves PENDING). */
    disposition_at: z.string().datetime().optional(),
    /** Optional outcome backref (set when the agent observes an outcome 1-4w later). */
    outcome_id: z.string().optional(),
});
